
import AxiosConfig from './LensAxiosConfig'
import firebase from '../config/FirebaseConfig'
import axios from 'axios'
import { providerKeyMap } from '@/utils/lens/dataSourceUtils'

let breakdownCancelToken = null
// Generates a payload to meet the requirements of its report type
const createGroupPayload = (query, isComparative) => {
  const { group, page, limit, sort, selectedRows, filters, currentWindow, comparativeQuery, selectedColumns, adAccountIds, platform = 'meta' } = query

  return {
    metadata: {
      provider: providerKeyMap[platform],
      group_by: group || 'ad_name',
      ...(page && { page }),
      ...(limit && { limit }),
      ...(sort && { sort }),
      ...((!isComparative && selectedRows) && { selected_rows: selectedRows }),
      ...(currentWindow && { attr_window: currentWindow })
    },
    query: [
      ...(filters ?? []),
      [{ key: 'account_id', operation: 'in', value: adAccountIds }]
    ],
    ...(selectedColumns && { selected_columns: selectedColumns }),
    // Comparaitive params
    ...(isComparative && comparativeQuery && { custom_group_queries: comparativeQuery })
  }
}

const createBreakdownPayload = (query, isComparative) => {
  return {
    metadata: {
      provider: providerKeyMap[query.platform],
      group_by: query?.groupBy ?? 'ad_name',
      page: 1,
      limit: 10,
      sort: {
        by: 'spend',
        order: 'desc'
      },
      attr_window: query.currentWindow
    },
    // query: query.filters || [],
    query: [
      ...(query.filters ?? []),
      [{ key: 'account_id', operation: 'in', value: query.adAccountIds }]
    ],
    ...(isComparative && { custom_group_queries: query.comparativeQuery || [] }),
    selected_columns: query.selectedColumns || []
  }
}

// Fetch AD
const getLensGroups = async (lensId, query, reportType, queryCancelToken) => {
  const { Aa: token } = firebase.auth().currentUser

  const isComparative = reportType === 'comparative'
  const payload = createGroupPayload(query, isComparative)

  console.log('Report Groups Query:', reportType, queryCancelToken)
  const AD_GROUPS_URL = !isComparative ? `/ads/groups/${lensId}` : `/ads/custom-groups/${lensId}`
  const { data } = await AxiosConfig.post(
    AD_GROUPS_URL,
    payload,
    {
      cancelToken: queryCancelToken,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  return data
}

const getLensGroupAds = async (lensId, query, reportType) => {
  const { Aa: token } = firebase.auth().currentUser
  const isComparative = reportType === 'comparative'
  // Cancel the previous request if it exists
  if (breakdownCancelToken) {
    breakdownCancelToken.cancel('New request incoming, cancelling previous one.')
  }

  breakdownCancelToken = axios.CancelToken.source()
  const payload = createBreakdownPayload(query, isComparative)

  const AD_GROUPS_URL = isComparative ? `ads/custom-group/${lensId}` : `/ads/group/${lensId}`

  const { data } = await AxiosConfig.post(
    AD_GROUPS_URL,
    payload,
    {
      cancelToken: breakdownCancelToken.token,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  return data
}

const getLensCreativeGroup = async (idPayload, query) => {
  const { Aa: token } = firebase.auth().currentUser
  const { lensId, creativeHash, adId } = idPayload

  const mappedQuery = {
    ...query,
    page: 1,
    limit: 10
  }

  const payload = createGroupPayload(mappedQuery)

  // Extend the payload query with the ad_id as an 'equals' filter
  if (creativeHash) {
    payload.query.push([{
      key: 'creative_hash',
      value: creativeHash,
      operation: 'equals'
    }])
  } else {
    payload.query.push([{
      key: 'ad_id',
      value: adId,
      operation: 'equals'
    }])
  }

  const { data } = await AxiosConfig.post(
    `/lenses/${lensId}/creative`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const abortBreakdownRequest = () => {
  if (breakdownCancelToken) {
    breakdownCancelToken.cancel('Breakdown request cancelled by user.')
  }
}

// Oauth facebook creative query
const getLensCreatives = async () => {
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.get('/lens-tokens/all-data', {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })

  return data
}

const deleteLensData = async () => {
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.delete('/lens-tokens/wipe', {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })

  return data
}

const createShareLink = async (creativePayload) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { provider, lensId, creativeId, creativeQuery, isPublic = true, adId } = creativePayload
  const payload = {
    provider,
    lens_id: lensId,
    creative_id: creativeId,
    is_public: isPublic,
    creative_query: creativeQuery,
    ad_id: adId
  }

  const { data } = await AxiosConfig.post(
    '/share/creatives',
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getShareCreative = async (id) => {
  const token = firebase?.auth()?.currentUser?.Aa
  const headers = token
    ? { Authorization: `Bearer ${token}` }
    : {}

  const { data } = await AxiosConfig.get(`/share/creatives/${id}`, {
    headers
  })

  return data
}

// Fetch ad details by ad_id only (used from comments page)
const getAdDetailsByAdId = async (lensId, adId, attrWindow) => {
  const { Aa: token } = firebase.auth().currentUser

  const payload = {
    metadata: {
      provider: 'facebook',
      group_by: 'ad_name',
      page: 1,
      limit: 10,
      attr_window: {
        click: attrWindow?.click || '7d',
        view: attrWindow?.view || '1d'
      }
    },
    query: [
      [
        {
          key: 'event_date',
          value: 'last_365_days',
          operation: 'greater_than'
        }
      ],
      [
        {
          key: 'event_date',
          value: 'today',
          operation: 'less_than'
        }
      ],
      [
        {
          key: 'ad_id',
          value: adId,
          operation: 'equals'
        }
      ]
    ]
  }

  const { data } = await AxiosConfig.post(
    `/lenses/${lensId}/creative`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

export default {
  getLensGroups,
  getLensGroupAds,
  getLensCreativeGroup,
  getAdDetailsByAdId,
  // Utils
  abortBreakdownRequest,

  getLensCreatives,
  deleteLensData,

  // Utils

  // Share
  createShareLink,
  getShareCreative,

  utils: {
    createGroupPayload
  }
}
